// A horizontal bar per dimension value, biggest first.
//
// This is the donut's replacement, and the reason is legibility at the sizes
// this app actually reaches: a bar list reads at twelve series where a donut
// does not, and it can carry the label, the figure and the share on one line
// without a leader line to anything.
//
// The figure is text on every row, so nothing here depends on telling two
// colours apart.

import type { ReactNode } from "react";
import styles from "./Chart.module.css";
import { axisMax } from "./scale.js";

export interface BreakdownRow {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly color: string;
  /** One line under the bar: what the row is short of, or what it is made of. */
  readonly hint?: string;
}

export interface BreakdownBarsProps {
  readonly rows: readonly BreakdownRow[];
  readonly formatValue: (value: number) => string;
  /**
   * The denominator of the share column. The TOTAL, not the sum of the rows: a
   * list that was cut by a limit must not report the part it shows as the whole.
   */
  readonly total: number;
  readonly label: string;
  readonly emptyMessage: string;
}

const BAR_WIDTH = 100;

export function BreakdownBars({
  rows,
  formatValue,
  total,
  label,
  emptyMessage,
}: BreakdownBarsProps): ReactNode {
  if (rows.length === 0) return <div className={styles.empty}>{emptyMessage}</div>;

  const widest = axisMax(Math.max(...rows.map((row) => row.value)));

  return (
    <ul className={styles.rows} aria-label={label}>
      {rows.map((row) => (
        <li key={row.key} className={styles.row}>
          <span className={styles.rowLabel} title={row.label}>
            {row.label}
          </span>
          <svg
            className={styles.rowBar}
            viewBox={`0 0 ${BAR_WIDTH} 10`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`${row.label}: ${formatValue(row.value)}`}
          >
            <rect
              x={0}
              y={0}
              rx={2}
              width={Math.max((row.value / widest) * BAR_WIDTH, row.value > 0 ? 0.5 : 0)}
              height={10}
              fill={row.color}
            />
          </svg>
          <span className={styles.rowValue}>{formatValue(row.value)}</span>
          <span className={styles.rowShare}>{shareOf(row.value, total)}</span>
          {row.hint !== undefined && <span className={styles.rowHint}>{row.hint}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * A share of nothing is not zero per cent, it is undefined — so a month that
 * cost nothing says so with a dash rather than reporting every row as 0.0%.
 */
function shareOf(value: number, total: number): string {
  if (total === 0) return "—";
  return `${((value / total) * 100).toFixed(1)}%`;
}
